import { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import {
  MapPin, Plus, Pencil, Trash2, Loader2, Save, X, Clock,
  DollarSign, GripVertical, ToggleLeft, ToggleRight,
} from "lucide-react";
import { deliveryZonasApi, DeliveryZona } from "../../lib/api";

export function DeliveryZonasPage() {
  const qc = useQueryClient();
  const [editando, setEditando] = useState<Partial<DeliveryZona> | null>(null);
  const [confirmDelete, setConfirmDelete] = useState<number | null>(null);

  const { data: zonas, isLoading } = useQuery({
    queryKey: ["delivery-zonas"],
    queryFn: () => deliveryZonasApi.list(),
  });

  const saveMut = useMutation({
    mutationFn: (z: Partial<DeliveryZona>) =>
      z.id ? deliveryZonasApi.actualizar(z.id, z) : deliveryZonasApi.crear(z),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["delivery-zonas"] });
      setEditando(null);
    },
  });

  const deleteMut = useMutation({
    mutationFn: (id: number) => deliveryZonasApi.eliminar(id),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["delivery-zonas"] });
      setConfirmDelete(null);
    },
  });

  const toggleMut = useMutation({
    mutationFn: (z: DeliveryZona) => deliveryZonasApi.actualizar(z.id, { activa: !z.activa }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["delivery-zonas"] }),
  });

  function nuevaZona() {
    setEditando({ nombre: "", costoEnvio: "0", activa: true, orden: (zonas?.length || 0) + 1 });
  }

  function guardar() {
    if (!editando?.nombre?.trim()) return;
    saveMut.mutate(editando);
  }

  return (
    <div className="max-w-3xl mx-auto">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-xl font-display font-bold text-ink-800 inline-flex items-center gap-2">
            <MapPin className="size-5 text-brand-600" /> Zonas de delivery
          </h1>
          <p className="text-sm text-ink-500 mt-1">
            Define las zonas de cobertura y el costo de envío para cada una.
          </p>
        </div>
        <button onClick={nuevaZona}
          className="px-4 py-2 rounded-lg bg-brand-600 hover:bg-brand-700 text-white text-sm font-medium inline-flex items-center gap-2">
          <Plus className="size-4" /> Nueva zona
        </button>
      </div>

      {isLoading ? (
        <div className="text-center py-12"><Loader2 className="size-6 animate-spin mx-auto text-ink-400" /></div>
      ) : !zonas || zonas.length === 0 ? (
        <div className="text-center py-16 bg-white rounded-2xl border border-ink-200">
          <MapPin className="size-10 text-ink-300 mx-auto mb-3" />
          <p className="text-sm text-ink-500">No hay zonas configuradas.</p>
          <p className="text-xs text-ink-400 mt-1">Crea zonas para que al hacer un pedido se auto-asigne el costo de envío.</p>
          <button onClick={nuevaZona}
            className="mt-4 px-4 py-2 rounded-lg bg-brand-600 hover:bg-brand-700 text-white text-sm font-medium inline-flex items-center gap-2">
            <Plus className="size-4" /> Crear primera zona
          </button>
        </div>
      ) : (
        <div className="space-y-2">
          {zonas.map((z) => (
            <div key={z.id} className={`bg-white rounded-xl border p-4 flex items-center gap-4 transition-opacity ${z.activa ? "border-ink-200" : "border-ink-100 opacity-60"}`}>
              <GripVertical className="size-4 text-ink-300 shrink-0" />
              <div className="flex-1 min-w-0">
                <div className="font-medium text-ink-800">{z.nombre}</div>
                {z.descripcion && <div className="text-xs text-ink-500 mt-0.5">{z.descripcion}</div>}
              </div>
              <div className="flex items-center gap-1 text-sm font-mono text-ink-700 shrink-0">
                <DollarSign className="size-3.5 text-emerald-600" />
                {Number(z.costoEnvio).toFixed(2)}
              </div>
              {z.tiempoEstimadoMin && (
                <div className="flex items-center gap-1 text-xs text-ink-500 shrink-0">
                  <Clock className="size-3" /> ~{z.tiempoEstimadoMin} min
                </div>
              )}
              <button onClick={() => toggleMut.mutate(z)} className="shrink-0"
                title={z.activa ? "Desactivar" : "Activar"}>
                {z.activa
                  ? <ToggleRight className="size-5 text-emerald-600" />
                  : <ToggleLeft className="size-5 text-ink-400" />}
              </button>
              <button onClick={() => setEditando(z)}
                className="p-1.5 rounded hover:bg-ink-100 text-ink-500 shrink-0">
                <Pencil className="size-3.5" />
              </button>
              {confirmDelete === z.id ? (
                <div className="flex items-center gap-1 shrink-0">
                  <button onClick={() => deleteMut.mutate(z.id)}
                    className="px-2 py-1 text-xs bg-red-600 text-white rounded font-medium">
                    {deleteMut.isPending ? <Loader2 className="size-3 animate-spin" /> : "Sí"}
                  </button>
                  <button onClick={() => setConfirmDelete(null)}
                    className="px-2 py-1 text-xs bg-ink-200 rounded">No</button>
                </div>
              ) : (
                <button onClick={() => setConfirmDelete(z.id)}
                  className="p-1.5 rounded hover:bg-red-50 text-red-500 shrink-0">
                  <Trash2 className="size-3.5" />
                </button>
              )}
            </div>
          ))}
        </div>
      )}

      {/* Modal editar/crear */}
      {editando && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50" onClick={() => setEditando(null)}>
          <div className="bg-white rounded-2xl shadow-xl max-w-md w-full" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between px-5 py-3 border-b border-ink-200">
              <h2 className="font-display font-bold text-ink-800">
                {editando.id ? "Editar zona" : "Nueva zona de delivery"}
              </h2>
              <button onClick={() => setEditando(null)} className="text-ink-400 hover:text-ink-700">
                <X className="size-5" />
              </button>
            </div>
            <div className="p-5 space-y-4">
              <div>
                <label className="block text-xs font-medium text-ink-600 mb-1">Nombre *</label>
                <input type="text" value={editando.nombre || ""} autoFocus
                  onChange={(e) => setEditando({ ...editando, nombre: e.target.value })}
                  placeholder="Ej: Municipio Baruta, Las Mercedes..."
                  className="input w-full" />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-xs font-medium text-ink-600 mb-1">Costo de envío (USD) *</label>
                  <input type="number" step="0.01" min="0"
                    value={editando.costoEnvio || ""}
                    onChange={(e) => setEditando({ ...editando, costoEnvio: e.target.value })}
                    className="input w-full" />
                </div>
                <div>
                  <label className="block text-xs font-medium text-ink-600 mb-1">Tiempo estimado (min)</label>
                  <input type="number" min="0"
                    value={editando.tiempoEstimadoMin || ""}
                    onChange={(e) => setEditando({ ...editando, tiempoEstimadoMin: e.target.value ? Number(e.target.value) : undefined })}
                    placeholder="30"
                    className="input w-full" />
                </div>
              </div>
              <div>
                <label className="block text-xs font-medium text-ink-600 mb-1">Descripción</label>
                <textarea rows={2}
                  value={editando.descripcion || ""}
                  onChange={(e) => setEditando({ ...editando, descripcion: e.target.value })}
                  placeholder="Sectores incluidos, límites, etc."
                  className="input w-full" />
              </div>
              <div>
                <label className="block text-xs font-medium text-ink-600 mb-1">Orden</label>
                <input type="number" min="0"
                  value={editando.orden ?? 0}
                  onChange={(e) => setEditando({ ...editando, orden: Number(e.target.value) })}
                  className="input w-20" />
              </div>

              {saveMut.isError && (
                <div className="px-3 py-2 bg-red-50 border border-red-200 rounded text-sm text-red-700">
                  Error al guardar
                </div>
              )}
            </div>
            <div className="px-5 py-3 border-t border-ink-200 bg-ink-50 flex justify-end gap-2 rounded-b-2xl">
              <button onClick={() => setEditando(null)} className="btn-secondary">Cancelar</button>
              <button onClick={guardar} disabled={saveMut.isPending || !editando.nombre?.trim()}
                className="px-4 py-2 rounded-lg bg-brand-600 hover:bg-brand-700 text-white text-sm font-medium inline-flex items-center gap-2 disabled:opacity-50">
                {saveMut.isPending ? <Loader2 className="size-4 animate-spin" /> : <Save className="size-4" />}
                Guardar
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
